// @ts-check
/**
 * SHIP-03/04: the pull request a work item or coding run shipped as. A chip
 * names its state (draft, open, merged, closed), its checks (passing,
 * failing, pending) and its review (approved, changes requested); the panel
 * adds the failing checks and "Ask bunny to fix".
 */
import { button } from "./ui/button.js";
import { h } from "./ui/dom.js";
import { badge } from "./ui/status.js";

/**
 * @typedef {{
 *   id: string, number: number | null, url: string | null,
 *   state: "creating" | "open" | "merged" | "closed" | "unknown",
 *   draft: boolean, checks: "none" | "pending" | "passing" | "failing",
 *   review: "none" | "approved" | "changes_requested",
 *   headCommit: string, failingChecks: string[],
 * }} PullRequestChip
 */

/** @param {PullRequestChip} pr */
export function pullRequestStates(pr) {
  /** @type {[string, import("./ui/status.js").Tone][]} */
  const states = [];
  if (pr.state === "merged") states.push(["Merged", "accent"]);
  else if (pr.state === "closed") states.push(["Closed", "neutral"]);
  else if (pr.state === "creating") states.push(["Opening", "waiting"]);
  else if (pr.state === "unknown") states.push(["Unconfirmed", "waiting"]);
  else states.push([pr.draft ? "Draft" : "Open", "info"]);
  if (pr.state === "open" || pr.state === "merged") {
    if (pr.checks === "passing") states.push(["Checks passing", "working"]);
    else if (pr.checks === "failing") states.push(["Checks failing", "failed"]);
    else if (pr.checks === "pending")
      states.push(["Checks pending", "waiting"]);
  }
  if (pr.review === "approved") states.push(["Approved", "working"]);
  else if (pr.review === "changes_requested")
    states.push(["Changes requested", "failed"]);
  return states;
}

/** A link to the pull request, when the forge gave one. @param {PullRequestChip} pr */
function link(pr) {
  const text = pr.number === null ? "Pull request" : `PR #${pr.number}`;
  return pr.url && /^https:\/\//u.test(pr.url)
    ? h("a", {
        href: pr.url,
        target: "_blank",
        rel: "noopener noreferrer",
        class: "pull-request-link",
        text,
      })
    : h("span", { class: "pull-request-link", text });
}

/** The compact chip: link and state badges. @param {PullRequestChip} pr */
export function pullRequestChip(pr) {
  return h(
    "span",
    {
      class: "pull-request-chip",
      dataset: { state: pr.state, checks: pr.checks, review: pr.review },
    },
    link(pr),
    ...pullRequestStates(pr).map(([text, tone]) => badge(text, tone)),
  );
}

/** Whether a person should look: failing checks or requested changes on an open PR. @param {PullRequestChip} pr */
export const pullRequestNeedsFix = (pr) =>
  pr.state === "open" &&
  (pr.checks === "failing" || pr.review === "changes_requested");

/**
 * The work item's pull request section.
 * @param {{ pr: PullRequestChip, canFix: boolean, onFix: () => Promise<void> | void }} options
 */
export function pullRequestPanel({ pr, canFix, onFix }) {
  const panel = h(
    "section",
    { class: "pull-request-panel", aria: { label: "Pull request" } },
    h("h3", { text: "Pull request" }),
    pullRequestChip(pr),
  );
  if (pr.failingChecks.length)
    panel.append(
      h("p", {
        class: "muted",
        text: `Failing: ${pr.failingChecks.join(", ")}`,
      }),
    );
  if (pr.state === "merged")
    panel.append(
      h("p", {
        class: "muted",
        text: "Merged. The item moves to Done unless its project keeps merged work open.",
      }),
    );
  if (pullRequestNeedsFix(pr) && canFix)
    panel.append(
      button({
        text: "Ask bunny to fix",
        icon: "code",
        variant: "secondary",
        size: "sm",
        onClick: () => void onFix(),
      }),
      h("p", {
        class: "muted",
        text: "Starts a follow-up coding run with the failing output. You review its result and push it to the same branch.",
      }),
    );
  return panel;
}
